// Minimal client-side error observability for a static SPA.
//
// There is no error-tracking service wired for this app today (see the
// waiver + rationale in AGENTS.md, "Observability"). Until one is adopted,
// this module is the single funnel for runtime errors:
//   1. structured console.error (visible in browser devtools / session tools)
//   2. an in-page ring buffer (`window.__backofficeErrors`, last 50) that
//      support can dump from the console of a live session
//   3. optional forwarding to REACT_APP_ERROR_ENDPOINT via sendBeacon/fetch
//      when an endpoint is configured (none is today — no-op when unset)
//
// Pattern follows the react_freekwent errorReporting util (fleet standard).

const MAX_BUFFERED = 50;

function getBuffer() {
  if (typeof window === 'undefined') return [];
  if (!Array.isArray(window.__backofficeErrors)) {
    window.__backofficeErrors = [];
  }
  return window.__backofficeErrors;
}

export function captureError(error, context = {}) {
  const entry = {
    ts: new Date().toISOString(),
    message: error?.message || String(error),
    stack: error?.stack || null,
    context,
    url: typeof window !== 'undefined' ? window.location.href : null,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
  };

  // eslint-disable-next-line no-console
  console.error('[backofficeui] error captured', entry);

  const buffer = getBuffer();
  buffer.push(entry);
  if (buffer.length > MAX_BUFFERED) buffer.splice(0, buffer.length - MAX_BUFFERED);

  const endpoint = process.env.REACT_APP_ERROR_ENDPOINT;
  if (endpoint) {
    try {
      const body = JSON.stringify(entry);
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        navigator.sendBeacon(endpoint, body);
      } else if (typeof fetch === 'function') {
        fetch(endpoint, { method: 'POST', body, keepalive: true }).catch(() => {});
      }
    } catch (_) {
      // Reporting must never throw into the app.
    }
  }

  return entry;
}

export function installGlobalErrorHandlers() {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (event) => {
    captureError(event.error || new Error(event.message), { source: 'window.onerror' });
  });
  window.addEventListener('unhandledrejection', (event) => {
    captureError(event.reason instanceof Error ? event.reason : new Error(String(event.reason)), {
      source: 'unhandledrejection',
    });
  });
}

export function getBufferedErrors() {
  return [...getBuffer()];
}

export default captureError;
